import React, { useCallback, useRef } from 'react';

type MainLinkRefElement = HTMLButtonElement | HTMLAnchorElement;

type UseAccessibleClickGroupReturn = {
  /**
   * Reference to the main link element inside the group which will handle the
   * click event on the group.
   */
  mainLinkRef: React.RefCallback<MainLinkRefElement>;
  /**
   * Click event handler to put on the group.
   */
  onGroupClick: (e: React.MouseEvent) => void;
};

/**
 * Handles the click event for a group of elements, reusing a link or button
 * inside the group to handle the click event.
 *
 * @see https://inclusive-components.design/cards/#theredundantclickevent
 * @see https://design-system.w3.org/components/cards.html#block-link-cards
 */
export const useAccessibleClickGroup = (): UseAccessibleClickGroupReturn => {
  const _mainLinkRef = useRef<MainLinkRefElement>(undefined);

  // We use a RefCallback instead of a RefObject to avoid TypeScript issues.
  const mainLinkRef = useCallback((element: MainLinkRefElement) => {
    _mainLinkRef.current = element;
  }, []);

  const onGroupClick = useCallback((e: React.MouseEvent) => {
    if (e.target === _mainLinkRef.current) {
      return;
    }

    _mainLinkRef.current?.click();
  }, []);

  return { mainLinkRef, onGroupClick };
};
